import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AssetCategory,
  AssetCategoryIconStyle,
  assetCategoryIconUrl,
  assetCategoryImagePath,
  hasAssetCategoryImage,
  PREDEFINED_ASSET_CATEGORY_ICON,
  resolvePredefinedAssetCategoryName
} from '@simOn/asset-categories/models';
import { MarkerIcons, MarkerStateIconService } from '@simOn/common/scan';
import { catchError, from, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { AssetCategoryImageService } from './asset-category-image.service';

export type { MarkerIcons, MarkerStateIcon } from '@simOn/common/scan';

/** Marker size in design px (Figma "3D icon" is a 64px circle). */
const MARKER_DESIGN_SIZE = 64;
/** Texture px per design px; 4x keeps the 1px border and 2px ring crisp when the marker is close to the camera. */
const PIXEL_RATIO = 4;
/** Rendered marker texture size in pixels. */
const MARKER_SIZE = MARKER_DESIGN_SIZE * PIXEL_RATIO;
/** Marker circle: `border: 1px solid var(--colors-text-icon, #313844); background: #FFF`. */
const MARKER_BORDER_WIDTH = 1;

/** Glyph size inside the marker circle (matches the 24/40 badge ratio used in the UI). */
const GLYPH_SIZE = Math.round(MARKER_SIZE * 0.59375);
const MARKER_BORDER_COLOR = '#313844';
const MARKER_BACKGROUND_COLOR = '#ffffff';

/**
 * Builds the sprite texture url for an asset category marker on the 3D walkthrough,
 * honoring the category `iconStyle` the same way the UI badges do:
 * - predefined categories keep the static `Circle_*.svg` sprites,
 * - `BlackWhite`: picked/custom icon on a white circle outlined with `MARKER_BORDER_COLOR`,
 * - `CustomColor`: `iconColor` tinted icon on an `iconColorBackground` circle outlined with `iconColor`,
 * - `CustomImage`: circle-cropped custom image scaled by `category.scale`, outlined with `MARKER_BORDER_COLOR`.
 * The custom image / uploaded SVG url comes from `AssetCategoryImageService`, so categories that arrive without
 * `imageUrlWithSasToken` (asset list, create / update asset responses) still render their image.
 * Results are rendered to data urls and cached per category appearance.
 */
@Injectable({
  providedIn: 'root'
})
export class AssetCategoryMarkerIconService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _markerStates = inject(MarkerStateIconService);
  private readonly _imageService = inject(AssetCategoryImageService);
  private readonly _cache = new Map<string, Observable<string>>();

  /** Default texture plus the shared "selected" halo (ring + band) drawn around it on the 3D walk. */
  markerIcons$(category: AssetCategory | null | undefined): Observable<MarkerIcons> {
    return this.markerIcon$(category).pipe(map((base) => this._markerStates.markerIcons(base)));
  }

  markerIcon$(category: AssetCategory | null | undefined): Observable<string> {
    if (!category) return of(this._predefinedMarkerUrl(category));

    const isDefaultStyle =
      category.iconStyle === AssetCategoryIconStyle.BlackWhite || category.iconStyle === AssetCategoryIconStyle.Unknown;
    if (isDefaultStyle && category.isPredefined && !hasAssetCategoryImage(category)) {
      return of(this._predefinedMarkerUrl(category));
    }

    // The blob path identifies the image; the SAS url differs between responses for the same image.
    const key = [
      category.iconStyle,
      category.icon,
      category.predefinedCategory,
      category.iconColor,
      category.iconColorBackground,
      assetCategoryImagePath(category) ?? category.imageUrlWithSasToken,
      category.scale
    ].join('|');

    let cached = this._cache.get(key);
    if (!cached) {
      cached = this._renderMarker$(category).pipe(
        catchError((error) => {
          console.warn(`Asset category marker fallback for "${category.name}":`, error);
          return of(this._predefinedMarkerUrl(null));
        }),
        shareReplay(1)
      );
      this._cache.set(key, cached);
    }
    return cached;
  }

  private _renderMarker$(category: AssetCategory): Observable<string> {
    return this._imageService
      .imageUrl(category)
      .pipe(switchMap((imageUrl) => this._renderMarkerWithImage$(category, imageUrl)));
  }

  private _renderMarkerWithImage$(category: AssetCategory, imageUrl: string | null): Observable<string> {
    if (category.iconStyle === AssetCategoryIconStyle.CustomImage && imageUrl) {
      return this._loadImage$(imageUrl).pipe(map((image) => this._drawImageMarker(image, category.scale ?? 1)));
    }

    const glyphUrl = imageUrl ?? assetCategoryIconUrl(category.icon ?? PREDEFINED_ASSET_CATEGORY_ICON.Other);
    const isColorStyle = category.iconStyle === AssetCategoryIconStyle.CustomColor;

    return this._loadImage$(glyphUrl).pipe(
      map((glyph) =>
        this._drawGlyphMarker(
          glyph,
          isColorStyle ? (category.iconColor ?? '#000000') : null,
          isColorStyle ? (category.iconColorBackground ?? MARKER_BACKGROUND_COLOR) : MARKER_BACKGROUND_COLOR,
          isColorStyle ? (category.iconColor ?? MARKER_BORDER_COLOR) : MARKER_BORDER_COLOR
        )
      )
    );
  }

  private _loadImage$(url: string): Observable<HTMLImageElement> {
    return this._httpClient.get(url, { responseType: 'blob' }).pipe(
      switchMap((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        return from(
          new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = objectUrl;
          })
        ).pipe(
          map((image) => {
            URL.revokeObjectURL(objectUrl);
            return image;
          })
        );
      })
    );
  }

  /** White/colored circle with an (optionally tinted) icon glyph in the center. */
  private _drawGlyphMarker(
    glyph: HTMLImageElement,
    glyphColor: string | null,
    background: string,
    borderColor: string
  ): string {
    const canvas = this._createMarkerCanvas(background, borderColor);
    const context = canvas.getContext('2d')!;
    const offset = (MARKER_SIZE - GLYPH_SIZE) / 2;

    if (glyphColor) {
      const tinted = document.createElement('canvas');
      tinted.width = tinted.height = GLYPH_SIZE;
      const tintedContext = tinted.getContext('2d')!;
      tintedContext.drawImage(glyph, 0, 0, GLYPH_SIZE, GLYPH_SIZE);
      tintedContext.globalCompositeOperation = 'source-in';
      tintedContext.fillStyle = glyphColor;
      tintedContext.fillRect(0, 0, GLYPH_SIZE, GLYPH_SIZE);
      context.drawImage(tinted, offset, offset);
    } else {
      context.drawImage(glyph, offset, offset, GLYPH_SIZE, GLYPH_SIZE);
    }

    return canvas.toDataURL('image/png');
  }

  /** Circle-cropped custom category image, scaled like the UI preview. */
  private _drawImageMarker(image: HTMLImageElement, scale: number): string {
    const canvas = this._createMarkerCanvas(MARKER_BACKGROUND_COLOR);
    const context = canvas.getContext('2d')!;

    const cover = Math.max(MARKER_SIZE / image.width, MARKER_SIZE / image.height) * scale;
    const width = image.width * cover;
    const height = image.height * cover;
    context.drawImage(image, (MARKER_SIZE - width) / 2, (MARKER_SIZE - height) / 2, width, height);

    return canvas.toDataURL('image/png');
  }

  /** Canvas pre-filled with a clipped marker circle outlined with a 1px (design) border. */
  private _createMarkerCanvas(background: string, borderColor: string = MARKER_BORDER_COLOR): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = MARKER_SIZE;
    const context = canvas.getContext('2d')!;
    const center = MARKER_SIZE / 2;
    const border = MARKER_BORDER_WIDTH * PIXEL_RATIO;

    context.beginPath();
    context.arc(center, center, center - border / 2, 0, Math.PI * 2);
    context.fillStyle = background;
    context.fill();
    context.lineWidth = border;
    context.strokeStyle = borderColor;
    context.stroke();
    context.clip();

    return canvas;
  }

  private _predefinedMarkerUrl(category: AssetCategory | null | undefined): string {
    const name = category ? resolvePredefinedAssetCategoryName(category.predefinedCategory) : null;
    return `/${$localize.locale}/assets/images/CATEGORIES_device_assets/circle/Circle_${name && name !== 'None' ? name : 'Other'}.svg`;
  }
}
